import React from 'react';
import { useParams } from 'react-router-dom';

import { useCompleteFragment } from '@eluve/apollo-client';
import { Grid, Heading, Stack, Text } from '@eluve/components';
import { addOneHour, getTimeDisplay } from '@eluve/date-utils';
import { activeAppointmentLayoutFragment } from '@eluve/frontend-appointment-hooks';
import { useUserName } from '@eluve/smart-blocks';

import { useChat } from './ConversationContextProvider';

interface ConversationInfoPanelProps {
  providerAvatarUrl?: string;
}

export const ConversationInfoPanel: React.FC<
  React.PropsWithChildren<ConversationInfoPanelProps>
> = ({ providerAvatarUrl, children }) => {
  const { appointmentId } = useParams() as { appointmentId: string };
  const appointment = useCompleteFragment({
    fragment: activeAppointmentLayoutFragment,
    key: {
      id: appointmentId,
    },
  });
  const userName = useUserName(appointment.userId);
  const providerName = `Dr. ${userName}`;
  const appointmentStartTime = appointment.startDate;
  const appointmentEndTime = appointment.endDate;
  const appointmentLocation = appointment.location?.name;
  const appointmentReason = appointment.patientReasonForVisit;
  const localStartTime = new Date(appointmentStartTime);
  const localEndTime = new Date(
    appointmentEndTime ? appointmentEndTime : addOneHour(localStartTime),
  );

  const calculatePosition = () => {
    if (!localStartTime || !localEndTime) return { top: '0', height: 'h-12' };

    const hour = localStartTime.getHours();
    const minutes = localStartTime.getMinutes();

    const firstDisplayedHour = localStartTime.getHours() - 1; // The first displayed hour
    const hourRelativeToFirst = hour - firstDisplayedHour;
    // 1 hour = 3rem
    const hourOffset = hourRelativeToFirst;
    const minuteOffset = Math.floor(minutes / 60); // Each 5 minutes is h-1

    const topPosition = (hourOffset + minuteOffset) * 3;

    // Calculate height based on duration
    const durationInMs = localEndTime.getTime() - localStartTime.getTime();
    const durationInMinutes = durationInMs / (1000 * 60);

    // Each 5 minutes is h-1, so 1 hour is h-12
    const heightClass = `h-${Math.min(Math.ceil(durationInMinutes / 5), 36)}`;

    return {
      top: `${topPosition}rem`,
      height: heightClass,
    };
  };

  const { top, height } = calculatePosition();

  const hoursToDisplay = 3;
  const timeSlots = Array.from({ length: hoursToDisplay }, (_, i) => {
    if (!localStartTime) return '';
    // Get the starting hour (1 hour before appointment, regardless of minutes)
    const startHour = localStartTime.getHours() - 1;
    const hour = startHour + i;
    const amPm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour; // Handle midnight as 12 AM
    return `${displayHour} ${amPm}`;
  });
  const { isConnected, isCompleted } = useChat();
  if (isConnected || isCompleted) return null;

  return (
    <Stack className="p-6" gap={6} fullHeight>
      <div className="w-fit rounded-lg bg-[#ffdca5] px-2.5 py-1.5 text-xs font-semibold text-[#8F5914]">
        New Patient
      </div>
      <Heading>Initial consultation</Heading>
      <Stack gap={3} direction="horizontal">
        <img
          src={providerAvatarUrl ?? 'path-to-image'}
          alt="Dr Avatar"
          className="size-6 rounded-full bg-white"
        />
        <Text>{providerName ?? 'Dr. Jane Doe'}</Text>
      </Stack>
      <Grid
        justify="start"
        align="start"
        className="grid-cols-[55px_1fr] rounded-lg border border-border-primary bg-bg-tertiary"
      >
        <div className="border-r border-inherit font-medium text-text-supporting">
          {timeSlots.map((timeSlot, index) => (
            <Stack
              key={index}
              className="h-12 py-2 pr-2.5"
              fullWidth={false}
              align="center"
              justify="end"
            >
              <Text color="tertiary" size="xs">
                {timeSlot}
              </Text>
            </Stack>
          ))}
        </div>
        <div className="relative border-border-primary">
          {Array.from({ length: hoursToDisplay * 2 }, (_, i) => (
            <div
              key={i}
              className={`h-6 ${i < hoursToDisplay * 2 - 1 ? 'border-b border-inherit' : ''}`}
            />
          ))}

          {localStartTime && localEndTime && (
            <div
              style={{ top }}
              className={`absolute ${height} w-full overflow-hidden border-l-2 border-black bg-white px-2 py-1.5 shadow-xs`}
            >
              <Text color="tertiary">
                {getTimeDisplay(localStartTime, localEndTime)}
              </Text>
              <div className="text-sm font-semibold text-text-primary">
                {appointmentLocation}
              </div>
            </div>
          )}
        </div>
      </Grid>
      <Stack className="rounded-lg bg-bg-tertiary p-4" gap={2}>
        <Text>
          <strong className="text-text-secondary">Reason for visit:</strong>
        </Text>
        <Text color="secondary">{appointmentReason ?? 'Demo Reason'}</Text>
      </Stack>
      {children}
    </Stack>
  );
};
